import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from './Icons';
import { getApiBase } from '../services/api';
import { getAuthToken } from '../utils/auth';

/**
 * Markdown Editor Modal
 * 
 * A fullscreen modal for editing markdown content with:
 * - Split view (editor + preview)
 * - Syntax highlighting for code blocks
 * - Basic markdown toolbar
 * - "View Full LLM Context" mode showing what the model sees
 * 
 * Uses React Portal to render at document body level,
 * ensuring it appears above all other content.
 */

// Cache for base tool specs from backend (doesn't include custom KBs)
let baseToolSpecsCache = null;

/**
 * Fetch base tool specs from the backend API (GET - all available tools)
 */
async function fetchBaseToolSpecs() {
  if (baseToolSpecsCache) return baseToolSpecsCache;
  
  try {
    const headers = {};
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${getApiBase()}/api/tools/specs`, { headers });
    if (!response.ok) throw new Error('Failed to fetch tool specs');
    const data = await response.json();
    baseToolSpecsCache = data;
    return data;
  } catch (error) {
    console.error('Error fetching base tool specs:', error);
    return { tools: {}, runtime_injections: [] };
  }
}

/**
 * Fetch agent-specific tool specs including custom KBs (POST)
 * This returns the REAL specs from Strands SDK including custom KB descriptions
 */
async function fetchAgentToolSpecs(tools = [], knowledgeBases = []) {
  try {
    const specsHeaders = { 'Content-Type': 'application/json' };
    const specsToken = getAuthToken();
    if (specsToken) specsHeaders['Authorization'] = `Bearer ${specsToken}`;
    const response = await fetch(`${getApiBase()}/api/tools/specs-for-agent`, {
      method: 'POST',
      headers: specsHeaders,
      body: JSON.stringify({
        tools: tools,
        knowledge_bases: knowledgeBases
      })
    });
    if (!response.ok) throw new Error('Failed to fetch agent tool specs');
    return await response.json();
  } catch (error) {
    console.error('Error fetching agent tool specs:', error);
    return { tools: {} };
  }
}

/**
 * Component to display a single tool's full specification
 * Shows description (with newlines preserved) and expandable JSON schema
 */
function ToolSpecDisplay({ tool }) {
  const [showSchema, setShowSchema] = useState(false);
  
  // Format description to preserve newlines (for namespace docs, filter docs, etc.)
  const formattedDescription = tool.description?.split('\n').map((line, i) => (
    <span key={i}>
      {line}
      {i < tool.description.split('\n').length - 1 && <br />}
    </span>
  ));
  
  return (
    <div className={`context-tool ${tool.autoInjected ? 'auto-injected' : ''}`}>
      <div className="context-tool-header">
        <span className="context-tool-name">{tool.name}</span>
        {tool.autoInjected && <span className="auto-badge">auto-injected</span>}
        {tool.category && <span className="category-badge">{tool.category}</span>}
      </div>
      <div className="context-tool-desc">{formattedDescription}</div>
      <div className="context-tool-params">
        <code>{tool.params}</code>
      </div>
      {tool.inputSchema && Object.keys(tool.inputSchema).length > 0 && (
        <div className="context-tool-schema">
          <button 
            className="schema-toggle"
            onClick={() => setShowSchema(!showSchema)}
          >
            {showSchema ? '▼ Hide JSON Schema' : '▶ Show Full JSON Schema (what LLM sees)'}
          </button>
          {showSchema && (
            <pre className="schema-json">
              {JSON.stringify(tool.inputSchema, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// Simple markdown to HTML converter (basic implementation)
function renderMarkdown(text) {
  if (!text) return '';
  
  let html = text
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Headers
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^# (.*$)/gim, '<h1>$1</h1>')
    // Bold
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/gim, '<em>$1</em>')
    // Code blocks
    .replace(/```(\w*)\n([\s\S]*?)```/gim, '<pre><code class="language-$1">$2</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/gim, '<code>$1</code>')
    // Lists
    .replace(/^\s*-\s+(.*$)/gim, '<li>$1</li>')
    // Line breaks
    .replace(/\n/gim, '<br/>');
  
  // Wrap consecutive <li> in <ul>
  html = html.replace(/(<li>.*?<\/li>)(<br\/>)?(<li>)/gim, '$1$3');
  html = html.replace(/(<li>.*<\/li>)/gim, '<ul>$1</ul>');
  html = html.replace(/<\/ul><br\/><ul>/gim, '');
  
  return html;
}

function MarkdownEditorModal({ 
  isOpen, 
  onClose, 
  value, 
  onChange, 
  title = "Edit System Prompt",
  tools = [],           // Array of tool IDs assigned to this agent
  agentName = "Agent",  // Name of the agent
  workflowType = "agent", // 'agent', 'swarm', or 'graph'
  knowledgeBases = []   // Custom KB configurations
}) {
  const [content, setContent] = useState(value || '');
  const [showPreview, setShowPreview] = useState(true);
  const [viewMode, setViewMode] = useState('edit'); // 'edit' or 'context'
  const [allToolSpecs, setAllToolSpecs] = useState(null);  // Combined specs (base + agent-specific)
  const [runtimeInjections, setRuntimeInjections] = useState([]);
  const [specsLoading, setSpecsLoading] = useState(false);
  const textareaRef = useRef(null);
  
  useEffect(() => {
    setContent(value || '');
  }, [value, isOpen]);
  
  // Fetch tool specs when switching to context view
  // Fetches both base specs (for runtime injections) and agent-specific specs (for custom KBs)
  useEffect(() => {
    if (viewMode === 'context' && !allToolSpecs) {
      setSpecsLoading(true);
      
      // Fetch both in parallel
      Promise.all([
        fetchBaseToolSpecs(),
        fetchAgentToolSpecs(tools, knowledgeBases)
      ]).then(([baseData, agentData]) => {
        // Merge: base tools + agent-specific (agent-specific takes precedence for custom KBs)
        const merged = { ...baseData.tools, ...agentData.tools };
        setAllToolSpecs(merged);
        setRuntimeInjections(baseData.runtime_injections || []);
        setSpecsLoading(false);
      });
    }
  }, [viewMode, allToolSpecs, tools, knowledgeBases]);
  
  useEffect(() => {
    if (isOpen) {
      // Focus textarea when modal opens (only in edit mode)
      if (viewMode === 'edit') {
      setTimeout(() => textareaRef.current?.focus(), 100);
      }
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, viewMode]);
  
  const handleSave = () => {
    onChange(content);
    onClose();
  };
  
  const handleCancel = () => {
    setContent(value || '');
    onClose();
  };
  
  const insertAtCursor = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.substring(start, end);
    const newContent = content.substring(0, start) + before + selected + after + content.substring(end);
    
    setContent(newContent);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  };
  
  if (!isOpen) return null;
  
  // Build the full context view showing what LLM sees
  const renderFullContext = () => {
    if (specsLoading) {
      return (
        <div className="llm-context-view">
          <div className="context-loading">
            <div className="spinner"></div>
            <p>Loading tool specifications from backend...</p>
          </div>
        </div>
      );
    }
    
    // Build tool list from the real specs we fetched
    const displayTools = [];
    const addedToolNames = new Set();
    
    // Add assigned tools (from the merged specs)
    tools.forEach(toolId => {
      const toolInfo = allToolSpecs?.[toolId];
      if (toolInfo && !addedToolNames.has(toolInfo.name)) {
        displayTools.push({
          name: toolInfo.name,
          description: toolInfo.description,
          params: toolInfo.parameters,
          category: toolInfo.category,
          autoInjected: toolInfo.auto_injected,
          inputSchema: toolInfo.input_schema
        });
        addedToolNames.add(toolInfo.name);
      }
    });
    
    // Add custom KB tools (should already be in allToolSpecs from the POST request)
    knowledgeBases.forEach(kb => {
      const kbToolName = kb.tool_name || `search_${kb.name?.toLowerCase().replace(/\s+/g, '_')}`;
      const toolInfo = allToolSpecs?.[kbToolName];
      if (toolInfo && !addedToolNames.has(toolInfo.name)) {
        displayTools.push({
          name: toolInfo.name,
          description: toolInfo.description,
          params: toolInfo.parameters,
          category: toolInfo.category || 'knowledge_bases',
          inputSchema: toolInfo.input_schema
        });
        addedToolNames.add(toolInfo.name);
      }
    });
    
    // Add handoff tool for swarm workflows (auto-injected by Strands SDK)
    if (workflowType === 'swarm' && !addedToolNames.has('handoff_to_agent')) {
      const handoffSpec = allToolSpecs?.['handoff_to_agent'];
      if (handoffSpec) {
        displayTools.push({
          name: handoffSpec.name,
          description: handoffSpec.description,
          params: handoffSpec.parameters,
          category: 'swarm',
          autoInjected: true,
          inputSchema: handoffSpec.input_schema
        });
        addedToolNames.add('handoff_to_agent');
      }
    }
    
    return (
      <div className="llm-context-view">
        <div className="context-section">
          <div className="context-section-header">
            <span className="context-icon">SYS</span>
            <span>System Prompt</span>
            <span className="context-badge">Sent every turn</span>
          </div>
          <div className="context-section-content">
            <pre className="context-code">{content || '(No system prompt defined)'}</pre>
          </div>
        </div>
        
        <div className="context-section">
          <div className="context-section-header">
            <span className="context-icon">FN</span>
            <span>Available Tools</span>
            <span className="context-badge">{displayTools.length} tools</span>
          </div>
          <div className="context-section-content">
            <div className="context-info">
              Tool definitions are sent to the LLM as JSON schema on every API call.
              The model can call any of these tools to perform actions.
            </div>
            {displayTools.length === 0 ? (
              <div className="context-empty">No tools assigned to this agent.</div>
            ) : (
              <div className="context-tools-list">
                {displayTools.map((tool, idx) => (
                  <ToolSpecDisplay key={idx} tool={tool} />
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="context-section">
          <div className="context-section-header">
            <span className="context-icon">MSG</span>
            <span>Conversation History</span>
            <span className="context-badge">Dynamic</span>
          </div>
          <div className="context-section-content">
            <div className="context-info">
              The conversation history grows with each turn. It includes:
            </div>
            <ul className="context-list">
              <li><strong>User messages</strong> - What the user asked</li>
              <li><strong>Assistant messages</strong> - Previous agent responses</li>
              <li><strong>Tool calls</strong> - Tools the agent called and their inputs</li>
              <li><strong>Tool results</strong> - Results returned from tool execution</li>
            </ul>
          </div>
        </div>
        
        <div className="context-section">
          <div className="context-section-header">
            <span className="context-icon">CTX</span>
            <span>Runtime Injections</span>
            <span className="context-badge">At runtime</span>
          </div>
          <div className="context-section-content">
            <div className="context-info">
              Additional context is injected at runtime based on configuration:
            </div>
            {runtimeInjections.length > 0 ? (
              <ul className="context-list">
                {runtimeInjections.map((injection, idx) => (
                  <li key={idx}>
                    <strong>{injection.name}</strong> - {injection.description}
                    <div className="injection-when">When: {injection.when}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="context-list">
                <li><strong>Project Memory</strong> - If enabled, project facts & decisions are appended to the system prompt</li>
                <li><strong>Swarm Handoffs</strong> - In Swarm mode, handoff context from previous agents is included</li>
                <li><strong>Conversation Manager</strong> - May summarize or truncate history based on settings</li>
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Use portal to render at document body level (escapes sidebar stacking context)
  return createPortal(
    <div className="markdown-modal-overlay">
      <div className="markdown-modal">
        {/* Header */}
        <div className="markdown-modal-header">
          <h2>{title}</h2>
          <div className="markdown-modal-actions">
            {/* View Mode Toggle */}
            <div className="view-mode-toggle">
              <button 
                className={`toggle-btn ${viewMode === 'edit' ? 'active' : ''}`}
                onClick={() => setViewMode('edit')}
              >
                Edit Prompt
              </button>
              <button 
                className={`toggle-btn ${viewMode === 'context' ? 'active' : ''}`}
                onClick={() => setViewMode('context')}
              >
                View Full LLM Context
              </button>
            </div>
            {viewMode === 'edit' && (
            <button 
              className={`btn btn-sm ${showPreview ? 'btn-secondary' : ''}`}
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </button>
            )}
            <button className="markdown-modal-close" onClick={handleCancel}>
              <CloseIcon size={16} />
            </button>
          </div>
        </div>
        
        {/* Toolbar - only show in edit mode */}
        {viewMode === 'edit' && (
        <div className="markdown-toolbar">
          <button className="toolbar-btn" onClick={() => insertAtCursor('# ')} title="Heading 1">
            H1
          </button>
          <button className="toolbar-btn" onClick={() => insertAtCursor('## ')} title="Heading 2">
            H2
          </button>
          <button className="toolbar-btn" onClick={() => insertAtCursor('### ')} title="Heading 3">
            H3
          </button>
          <div className="toolbar-divider" />
          <button className="toolbar-btn" onClick={() => insertAtCursor('**', '**')} title="Bold">
            <strong>B</strong>
          </button>
          <button className="toolbar-btn" onClick={() => insertAtCursor('*', '*')} title="Italic">
            <em>I</em>
          </button>
          <button className="toolbar-btn" onClick={() => insertAtCursor('`', '`')} title="Inline Code">
            {'</>'}
          </button>
          <div className="toolbar-divider" />
          <button className="toolbar-btn" onClick={() => insertAtCursor('- ')} title="List Item">
            •
          </button>
          <button className="toolbar-btn" onClick={() => insertAtCursor('```\n', '\n```')} title="Code Block">
            {'{ }'}
          </button>
        </div>
        )}
        
        {/* Edit Mode Content */}
        {viewMode === 'edit' && (
        <div className={`markdown-content ${showPreview ? 'split' : 'full'}`}>
          {/* Editor */}
          <div className="markdown-editor">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your system prompt here using Markdown..."
              spellCheck={false}
            />
          </div>
          
          {/* Preview */}
          {showPreview && (
            <div className="markdown-preview">
              <div 
                className="markdown-preview-content"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
              />
            </div>
          )}
        </div>
        )}
        
        {/* Context View Mode */}
        {viewMode === 'context' && (
          <div className="markdown-content full context-mode">
            {renderFullContext()}
          </div>
        )}
        
        {/* Footer */}
        <div className="markdown-modal-footer">
          {viewMode === 'edit' ? (
            <>
          <div className="markdown-stats">
            {content.length} characters • {content.split(/\s+/).filter(Boolean).length} words
          </div>
          <div className="markdown-modal-buttons">
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Save Changes
            </button>
          </div>
            </>
          ) : (
            <>
              <div className="markdown-stats context-stats">
                This is a read-only view of what the LLM receives on each API call.
              </div>
              <div className="markdown-modal-buttons">
                <button className="btn btn-secondary" onClick={() => setViewMode('edit')}>
                  Back to Edit
                </button>
                <button className="btn btn-primary" onClick={handleCancel}>
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MarkdownEditorModal;


